import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useState, useRef, useEffect } from 'react';

const LanguageContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const Flag = styled.span`
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  line-height: 1;
  transform: translateY(-1px);
`;

const LanguageButton = styled.button`
  background: var(--surface-color);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  color: var(--primary-color);
  cursor: pointer;
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  height: 38px;
  gap: 0.25rem;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  line-height: 1;

  &:hover {
    border-color: var(--primary-color);
    box-shadow: 0 0 10px var(--primary-glow);
  }

  &::after {
    content: '▼';
    font-size: 0.8rem;
    margin-left: 0.5rem;
  }
`;

const DropdownMenu = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 0.5rem;
  background: var(--surface-color);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 0.5rem;
  display: ${props => props.isOpen ? 'block' : 'none'};
  backdrop-filter: blur(10px);
  z-index: 1000;
  min-width: 100%;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
`;

const LanguageOption = styled.button`
  background: none;
  border: none;
  color: var(--text-primary);
  padding: 0.5rem 1rem;
  width: 100%;
  text-align: left;
  cursor: pointer;
  border-radius: 4px;
  display: flex;
  align-items: center;
  height: 2rem;
  gap: 0.5rem;
  transition: all 0.2s ease;
  line-height: 1;

  & ${Flag} {
    width: 1.5rem;
  }

  &:hover {
    background: var(--primary-glow);
    color: var(--primary-color);
  }

  &.active {
    color: var(--primary-color);
  }
`;

export default function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const languages = [
    { code: 'zh', name: '中文', flag: '🇨🇳' },
    { code: 'en', name: 'English', flag: '🇺🇸' }
  ];

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = (langCode: string) => {
    i18n.changeLanguage(langCode);
    setIsOpen(false);
  };

  return (
    <LanguageContainer ref={containerRef}>
      <LanguageButton onClick={() => setIsOpen(!isOpen)}>
        <Flag>{currentLanguage.flag}</Flag>
        {currentLanguage.name}
      </LanguageButton>
      <DropdownMenu isOpen={isOpen}>
        {languages.map(lang => (
          <LanguageOption
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            className={lang.code === i18n.language ? 'active' : ''}
          >
            <Flag>{lang.flag}</Flag>
            {lang.name}
          </LanguageOption>
        ))}
      </DropdownMenu>
    </LanguageContainer>
  );
} 